import { useState, useEffect } from 'react';
import { X, Sparkles, Loader, MapPin, ChefHat, Truck, UtensilsCrossed, Star, Clock, Users, RefreshCw, Check } from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurpriseMeModal = ({ isOpen, onClose }) => {
  const { locationString } = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPick, setSelectedPick] = useState(null);

  const fetchSurprise = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/smart/surprise-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: locationString || 'your city'
        })
      });
      
      if (!res.ok) throw new Error('Failed to get surprise');
      const result = await res.json();
      setData(result);
      setSelectedPick(result.ai_pick);
    } catch (err) {
      setError('Could not generate recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !data) {
      fetchSurprise();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setSelectedPick(null);
      setError(null);
    }
  }, [isOpen]);

  const CategoryCard = ({ type, rec, isAIPick }) => {
    if (!rec) return null;
    
    const config = {
      dine_out: { icon: UtensilsCrossed, label: 'Go Out', color: 'from-rose-500 to-pink-500' },
      order_in: { icon: Truck, label: 'Order In', color: 'from-blue-500 to-cyan-500' },
      cook_at_home: { icon: ChefHat, label: 'Cook It', color: 'from-green-500 to-emerald-500' }
    };
    
    const { icon: Icon, label, color } = config[type] || { icon: Sparkles, label: 'Option', color: 'from-gray-500 to-gray-600' };
    const isSelected = selectedPick === type;

    return (
      <div 
        onClick={() => setSelectedPick(type)}
        className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
          isSelected ? 'ring-2 ring-[#FF6B35] scale-[1.02]' : 'hover:scale-[1.01]'
        }`}
      >
        {/* AI Pick Badge */}
        {isAIPick && (
          <div className="absolute top-3 right-3 z-10 bg-[#FF6B35] text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            AI Pick
          </div>
        )}
        
        {/* Selected Check */}
        {isSelected && (
          <div className="absolute top-3 left-3 z-10 w-6 h-6 bg-[#FF6B35] rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}

        {/* Image */}
        <div className="relative h-40">
          <img 
            src={rec.image} 
            alt={rec.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=75'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Category Label */}
          <div className={`absolute bottom-3 left-3 px-3 py-1.5 bg-gradient-to-r ${color} rounded-full flex items-center gap-1.5`}>
            <Icon className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">{label}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 bg-white">
          <h3 className="font-bold text-gray-900 mb-1">{rec.name}</h3>
          {rec.venue_name && (
            <p className="text-sm text-gray-600 mb-2">{rec.venue_name}</p>
          )}
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{rec.description}</p>
          
          {/* Meta Info */}
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
            {rec.price && (
              <span className="font-medium text-gray-700">{rec.price}</span>
            )}
            {rec.rating && (
              <span className="flex items-center gap-0.5">
                <Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />
                {rec.rating}
              </span>
            )}
            {rec.distance && (
              <span className="flex items-center gap-0.5">
                <MapPin className="w-3 h-3" />
                {rec.distance}
              </span>
            )}
            {rec.delivery_time && (
              <span className="flex items-center gap-0.5">
                <Clock className="w-3 h-3" />
                {rec.delivery_time}
              </span>
            )}
            {rec.prep_time && (
              <span className="flex items-center gap-0.5">
                <Clock className="w-3 h-3" />
                {rec.prep_time}
              </span>
            )}
          </div>

          {/* Why This */}
          <p className="text-sm text-[#FF6B35] font-medium">{rec.why_this}</p>
          
          {/* Key Ingredients for recipes */}
          {rec.key_ingredients && (
            <div className="mt-2 flex flex-wrap gap-1">
              {rec.key_ingredients.slice(0, 4).map((ing, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {ing}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-4xl bg-gradient-to-b from-[#FFF5F0] to-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <span className="text-3xl">{data?.theme_emoji || '🎲'}</span>
              </div>
              <div>
                <h2 className="text-white font-bold text-2xl">
                  {loading ? 'Finding your surprise...' : data?.theme || 'Surprise Me'}
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {data?.intro || 'Let AI decide what you should eat'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={(e) => { e.stopPropagation(); fetchSurprise(); }}
                disabled={loading}
                className="p-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                title="Get new recommendations"
              >
                <RefreshCw className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={onClose}
                className="p-2.5 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          {locationString && (
            <div className="flex items-center gap-1.5 mt-4 text-white/70 text-sm">
              <MapPin className="w-3.5 h-3.5" />
              <span>Recommendations for {locationString}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] rounded-full flex items-center justify-center mb-4 animate-pulse">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-600 font-medium">AI is curating your perfect mix...</p>
              <p className="text-gray-400 text-sm mt-1">This takes a few seconds</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={fetchSurprise}
                className="px-6 py-2 bg-[#FF6B35] text-white rounded-full font-medium hover:bg-[#E85D04] transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {data && !loading && (
            <>
              {/* AI Pick Reason */}
              {data.ai_pick_reason && (
                <div className="bg-[#FFF5F0] rounded-2xl p-4 mb-6 flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">AI's Recommendation</p>
                    <p className="text-sm text-gray-600 mt-0.5">{data.ai_pick_reason}</p>
                  </div>
                </div>
              )}

              {/* Options Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CategoryCard 
                  type="dine_out" 
                  rec={data.mix?.dine_out} 
                  isAIPick={data.ai_pick === 'dine_out'}
                />
                <CategoryCard 
                  type="order_in" 
                  rec={data.mix?.order_in} 
                  isAIPick={data.ai_pick === 'order_in'}
                />
                <CategoryCard 
                  type="cook_at_home" 
                  rec={data.mix?.cook_at_home} 
                  isAIPick={data.ai_pick === 'cook_at_home'}
                />
              </div>

              {/* Action Button */}
              <div className="mt-6 flex justify-center">
                <button 
                  onClick={onClose}
                  className="px-8 py-3 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Go with {selectedPick === 'dine_out' ? 'Dining Out' : selectedPick === 'order_in' ? 'Ordering In' : 'Cooking'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurpriseMeModal;
